import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Building2, CreditCard, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import {
  useAdminTeamDetail,
  useSuspendTeam,
  useReactivateTeam,
  useInviteTeamMember,
  useCancelTeamInvitation,
  useSuspendTeamMember,
  useReactivateTeamMember,
  useRemoveTeamMember,
} from "@/lib/api/teams.queries";
import {
  INVITABLE_ROLES,
  type AdminTeamInvitation,
  type AdminTeamMember,
  type InvitableRole,
} from "@/lib/api/teams";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Table, THead, TH, TBody, TR, TD } from "@/components/ui/Table";
import { CenteredSpinner } from "@/components/ui/Spinner";
import { ErrorState } from "@/components/ui/ErrorState";
import { Badge, StatusBadge, CertStatusBadge } from "@/components/ui/Badge";
import { Dialog } from "@/components/ui/Dialog";
import { Input, Label, Select } from "@/components/ui/Input";
import { formatDate, maskPhone } from "@/lib/utils";
import { cn } from "@/lib/utils";

type Tab = "miembros" | "empresas" | "suscripcion";

type MemberAction = { kind: "suspend" | "remove"; member: AdminTeamMember };

const ROLE_LABELS: Record<InvitableRole, string> = {
  admin: "admin — gestiona el equipo",
  accountant: "accountant — crea y emite documentos",
  viewer: "viewer — solo lectura",
};

function MemberStatusBadge({ status }: { status: AdminTeamMember["status"] }) {
  const tone = status === "active" ? "success" : status === "suspended" ? "danger" : "neutral";
  return <Badge tone={tone}>{status}</Badge>;
}

/** A pending invitation past its expiry is effectively expired — show that. */
function effectiveInvitationStatus(inv: AdminTeamInvitation): string {
  return inv.status === "pending" && new Date(inv.expiresAt) <= new Date()
    ? "expired"
    : inv.status;
}

function InvitationStatusBadge({ status }: { status: string }) {
  const tone =
    status === "pending"
      ? "info"
      : status === "accepted"
        ? "success"
        : status === "expired"
          ? "warning"
          : "neutral";
  return <Badge tone={tone}>{status}</Badge>;
}

export function TeamDetail() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("miembros");
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invitePhone, setInvitePhone] = useState("");
  const [inviteRole, setInviteRole] = useState<InvitableRole>("accountant");
  const [memberAction, setMemberAction] = useState<MemberAction | null>(null);

  const query = useAdminTeamDetail(teamId);
  const suspendMutation = useSuspendTeam();
  const reactivateMutation = useReactivateTeam();
  const inviteMember = useInviteTeamMember();
  const cancelInvitation = useCancelTeamInvitation();
  const suspendMember = useSuspendTeamMember();
  const reactivateMember = useReactivateTeamMember();
  const removeMember = useRemoveTeamMember();

  async function onSuspend() {
    if (!teamId) return;
    try {
      await suspendMutation.mutateAsync({ teamId, reason: suspendReason });
      toast.success("Equipo suspendido.");
      setSuspendOpen(false);
      setSuspendReason("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al suspender equipo.");
    }
  }

  async function onReactivate() {
    if (!teamId) return;
    try {
      await reactivateMutation.mutateAsync(teamId);
      toast.success("Equipo reactivado.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al reactivar equipo.");
    }
  }

  async function onInviteMember() {
    if (!teamId) return;
    try {
      await inviteMember.mutateAsync({ teamId, phone: invitePhone.trim(), role: inviteRole });
      toast.success("Invitación enviada por WhatsApp.");
      setInviteOpen(false);
      setInvitePhone("");
      setInviteRole("accountant");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al enviar la invitación.");
    }
  }

  async function onCancelInvitation(invitationId: string) {
    if (!teamId) return;
    try {
      await cancelInvitation.mutateAsync({ teamId, invitationId });
      toast.success("Invitación cancelada.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al cancelar la invitación.");
    }
  }

  async function onReactivateMember(member: AdminTeamMember) {
    if (!teamId) return;
    try {
      await reactivateMember.mutateAsync({ teamId, userId: member.userId });
      toast.success("Miembro reactivado.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al reactivar el miembro.");
    }
  }

  async function onConfirmMemberAction() {
    if (!teamId || !memberAction) return;
    const { kind, member } = memberAction;
    try {
      if (kind === "suspend") {
        await suspendMember.mutateAsync({ teamId, userId: member.userId });
        toast.success("Miembro suspendido.");
      } else {
        await removeMember.mutateAsync({ teamId, userId: member.userId });
        toast.success("Miembro quitado del equipo.");
      }
      setMemberAction(null);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : kind === "suspend"
            ? "Error al suspender el miembro."
            : "Error al quitar el miembro.",
      );
    }
  }

  if (query.isPending) return <CenteredSpinner label="Cargando equipo…" />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const t = query.data;

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: "miembros", label: "Miembros", icon: <Users className="h-3.5 w-3.5" /> },
    { key: "empresas", label: "Empresas", icon: <Building2 className="h-3.5 w-3.5" /> },
    { key: "suscripcion", label: "Suscripción", icon: <CreditCard className="h-3.5 w-3.5" /> },
  ];

  return (
    <div className="space-y-6">
      {/* Back + header */}
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate("/teams")} className="mb-3">
          <ArrowLeft className="h-3.5 w-3.5" /> Volver a equipos
        </Button>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-slate-900">{t.name}</h1>
              <StatusBadge status={t.status} />
            </div>
            <p className="text-sm text-slate-500 mt-0.5 font-mono">{t.slug}</p>
            <p className="text-xs text-slate-500 mt-1">Creado {formatDate(t.createdAt)}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {t.status === "active" ? (
              <Button variant="danger" size="sm" onClick={() => setSuspendOpen(true)}>
                Suspender equipo
              </Button>
            ) : t.status === "suspended" ? (
              <Button
                variant="outline"
                size="sm"
                onClick={onReactivate}
                loading={reactivateMutation.isPending}
              >
                Reactivar equipo
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 flex gap-0.5">
        {tabs.map(({ key, label, icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={cn(
              "flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
              tab === key
                ? "border-brand-600 text-brand-700"
                : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
            )}
          >
            {icon}
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "miembros" && (
        <div className="space-y-4">
          <Card className="overflow-hidden">
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <CardTitle>
                  Miembros ({t.members.filter((m) => m.status === "active").length} activos)
                </CardTitle>
                <Button size="sm" onClick={() => setInviteOpen(true)}>
                  <UserPlus className="h-3.5 w-3.5" /> Invitar miembro
                </Button>
              </div>
            </CardHeader>
            <Table>
              <THead>
                <tr>
                  <TH>Usuario</TH>
                  <TH>Teléfono</TH>
                  <TH>Rol</TH>
                  <TH>Estado</TH>
                  <TH>Ingresó</TH>
                  <TH>{""}</TH>
                </tr>
              </THead>
              <TBody>
                {t.members.map((m) => (
                  <TR key={m.userId} className={cn(m.status === "removed" && "opacity-60")}>
                    <TD>
                      <p className="font-medium text-slate-900">{m.displayName ?? "—"}</p>
                      <p className="text-[11px] font-mono text-slate-500">{m.userId}</p>
                    </TD>
                    <TD className="font-mono text-xs">{m.phone ? maskPhone(m.phone) : "—"}</TD>
                    <TD>
                      <span className="text-xs capitalize">{m.role}</span>
                    </TD>
                    <TD>
                      <MemberStatusBadge status={m.status} />
                    </TD>
                    <TD className="text-[11px] text-slate-500">{formatDate(m.joinedAt)}</TD>
                    <TD>
                      <div className="flex items-center justify-end gap-1.5">
                        {m.status === "active" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setMemberAction({ kind: "suspend", member: m })}
                          >
                            Suspender
                          </Button>
                        )}
                        {m.status === "suspended" && (
                          <Button
                            variant="outline"
                            size="sm"
                            loading={
                              reactivateMember.isPending &&
                              reactivateMember.variables?.userId === m.userId
                            }
                            onClick={() => onReactivateMember(m)}
                          >
                            Reactivar
                          </Button>
                        )}
                        {m.status !== "removed" && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => setMemberAction({ kind: "remove", member: m })}
                          >
                            Quitar
                          </Button>
                        )}
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </Card>

          <Card className="overflow-hidden">
            <CardHeader>
              <CardTitle>
                Invitaciones (
                {t.invitations.filter((i) => effectiveInvitationStatus(i) === "pending").length}{" "}
                pendientes)
              </CardTitle>
            </CardHeader>
            {t.invitations.length === 0 ? (
              <CardContent>
                <p className="text-sm text-slate-500">No hay invitaciones para este equipo.</p>
              </CardContent>
            ) : (
              <Table>
                <THead>
                  <tr>
                    <TH>Teléfono</TH>
                    <TH>Rol</TH>
                    <TH>Estado</TH>
                    <TH>Enviada</TH>
                    <TH>Expira</TH>
                    <TH>{""}</TH>
                  </tr>
                </THead>
                <TBody>
                  {t.invitations.map((inv) => {
                    const status = effectiveInvitationStatus(inv);
                    return (
                      <TR key={inv.id}>
                        <TD className="font-mono text-xs">{inv.phone}</TD>
                        <TD>
                          <span className="text-xs capitalize">{inv.role}</span>
                        </TD>
                        <TD>
                          <InvitationStatusBadge status={status} />
                        </TD>
                        <TD className="text-[11px] text-slate-500">{formatDate(inv.createdAt)}</TD>
                        <TD className="text-[11px] text-slate-500">{formatDate(inv.expiresAt)}</TD>
                        <TD>
                          <div className="flex justify-end">
                            {status === "pending" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                loading={
                                  cancelInvitation.isPending &&
                                  cancelInvitation.variables?.invitationId === inv.id
                                }
                                onClick={() => onCancelInvitation(inv.id)}
                              >
                                Cancelar
                              </Button>
                            )}
                          </div>
                        </TD>
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
            )}
          </Card>
        </div>
      )}

      {tab === "empresas" && (
        <Card className="overflow-hidden">
          <CardHeader>
            <CardTitle>Empresas ({t.companies.length})</CardTitle>
          </CardHeader>
          <Table>
            <THead>
              <tr>
                <TH>NIT</TH>
                <TH>Razón social</TH>
                <TH>Certificado</TH>
                <TH>Ambiente DIAN</TH>
                <TH>Creada</TH>
              </tr>
            </THead>
            <TBody>
              {t.companies.map((c) => (
                <TR key={c.nit} className="cursor-pointer" onClick={() => navigate(`/companies/${c.nit}`)}>
                  <TD className="font-mono text-xs">{c.nit}</TD>
                  <TD className="font-medium text-slate-900">{c.legalName}</TD>
                  <TD>
                    <CertStatusBadge status={c.certStatus ?? "missing"} />
                  </TD>
                  <TD>
                    <span className={cn(
                      "text-xs font-medium px-1.5 py-0.5 rounded",
                      c.dianEnvironment === "prod" ? "bg-red-50 text-red-700" : "bg-sky-50 text-sky-700"
                    )}>
                      {c.dianEnvironment}
                    </span>
                  </TD>
                  <TD className="text-[11px] text-slate-500">{formatDate(c.createdAt)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}

      {tab === "suscripcion" && (
        <div className="space-y-4">
          {t.recentSubscriptions.length === 0 ? (
            <Card>
              <CardContent>
                <p className="text-sm text-slate-500">No hay suscripciones para este equipo.</p>
              </CardContent>
            </Card>
          ) : (
            t.recentSubscriptions.map((s) => (
              <Card key={s.id}>
                <CardContent className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-slate-900 font-mono">{s.id}</p>
                    <span className="text-xs capitalize bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">
                      {s.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
                    <p>Plan: <span className="text-slate-900">{s.plan}</span></p>
                    {s.currentPeriodEnd && (
                      <p>Vence: <span className="text-slate-900">{formatDate(s.currentPeriodEnd)}</span></p>
                    )}
                    {s.lastPaymentAt && (
                      <p>Último pago: <span className="text-slate-900">{formatDate(s.lastPaymentAt)}</span></p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Suspend dialog */}
      <Dialog
        open={suspendOpen}
        onClose={() => { setSuspendOpen(false); setSuspendReason(""); }}
        title={`Suspender equipo: ${t.name}`}
        description="El equipo quedará suspendido y sus miembros no podrán acceder a la plataforma."
      >
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-700">Motivo</label>
            <textarea
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              rows={3}
              placeholder="Describe el motivo de la suspensión…"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => { setSuspendOpen(false); setSuspendReason(""); }}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={onSuspend}
              loading={suspendMutation.isPending}
              disabled={!suspendReason.trim()}
            >
              Confirmar suspensión
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Invite member dialog */}
      <Dialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title={`Invitar miembro a ${t.name}`}
        description="La persona recibirá la invitación por WhatsApp y entrará al equipo al aceptarla."
      >
        <div className="space-y-3">
          <div>
            <Label htmlFor="invite-phone" required>
              Celular (Colombia)
            </Label>
            <Input
              id="invite-phone"
              className="mt-1"
              value={invitePhone}
              onChange={(e) => setInvitePhone(e.target.value)}
              placeholder="3001234567"
              inputMode="tel"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="invite-role" required>
              Rol
            </Label>
            <Select
              id="invite-role"
              className="mt-1"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as InvitableRole)}
            >
              {INVITABLE_ROLES.map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABELS[role]}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setInviteOpen(false)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={onInviteMember}
              loading={inviteMember.isPending}
              disabled={invitePhone.trim().length < 10}
            >
              Enviar invitación
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Suspend / remove member confirmation */}
      <Dialog
        open={memberAction !== null}
        onClose={() => setMemberAction(null)}
        title={
          memberAction?.kind === "suspend"
            ? "Suspender miembro"
            : "Quitar miembro del equipo"
        }
        description={
          memberAction?.kind === "suspend"
            ? "La persona perderá el acceso a este equipo hasta que la reactives."
            : "La persona saldrá del equipo. Podrás volver a invitarla más adelante."
        }
      >
        {memberAction && (
          <div className="space-y-3">
            <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
              <p className="text-sm font-medium text-slate-900">
                {memberAction.member.displayName ?? "—"}
              </p>
              <p className="text-xs font-mono text-slate-500">
                {memberAction.member.phone ? maskPhone(memberAction.member.phone) : ""}{" "}
                · rol {memberAction.member.role}
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setMemberAction(null)}>
                Cancelar
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={onConfirmMemberAction}
                loading={suspendMember.isPending || removeMember.isPending}
              >
                {memberAction.kind === "suspend" ? "Confirmar suspensión" : "Confirmar retiro"}
              </Button>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}
